/**
 * Visual rule-builder ↔ JSON-Logic compiler.
 *
 * Builder model: an AND-list of simple conditions.
 *   { field: "type", op: "eq", value: "feedback" }
 *   { field: "severity", op: "eq", value: "urgent" }
 *
 * Compiles to: { "and": [ {"==": [{"var": "type"}, "feedback"]}, ... ] }
 *
 * Power users can edit the JSON directly via the Advanced accordion; the
 * builder only handles the common case. If parsing JSON fails to round-trip
 * through the builder shape, we fall back to "Advanced (JSON Logic)" mode
 * and hide the visual builder.
 */

export type RuleOperator =
  | 'eq'
  | 'neq'
  | 'contains_ci'
  | 'starts_with'
  | 'ends_with'
  | 'regex'
  | 'exists'
  | 'not_exists';

export type RuleCondition = {
  field: string;
  op: RuleOperator;
  value: string;
};

export const OPERATOR_LABELS: Record<RuleOperator, string> = {
  eq: 'equals',
  neq: 'does not equal',
  contains_ci: 'contains',
  starts_with: 'starts with',
  ends_with: 'ends with',
  regex: 'matches regex',
  exists: 'exists (is set)',
  not_exists: 'is empty',
};

export const COMMON_FIELDS = [
  'type',
  'title',
  'body',
  'severity',
  'url',
  'actor.email',
  'metadata.priority',
  'metadata.environment',
] as const;

function varExpr(field: string) {
  return { var: field } as const;
}

function compileCondition(c: RuleCondition): unknown {
  const v = varExpr(c.field);
  switch (c.op) {
    case 'eq':
      return { '==': [v, c.value] };
    case 'neq':
      return { '!=': [v, c.value] };
    case 'contains_ci':
      return { contains_ci: [v, c.value] };
    case 'starts_with':
      return { regex: [v, `^${escapeRegex(c.value)}`, 'u'] };
    case 'ends_with':
      return { regex: [v, `${escapeRegex(c.value)}$`, 'u'] };
    case 'regex':
      return { regex: [v, c.value, 'u'] };
    case 'exists':
      return { '!!': v };
    case 'not_exists':
      return { '!': v };
  }
}

function escapeRegex(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function compileMatchJson(conditions: RuleCondition[]): string {
  if (conditions.length === 0) return JSON.stringify(true);
  if (conditions.length === 1) return JSON.stringify(compileCondition(conditions[0]!));
  return JSON.stringify({ and: conditions.map(compileCondition) });
}

/**
 * Best-effort reverse: parse JSON-Logic AND-list of simple equality/regex into
 * builder conditions. Returns `null` if the JSON doesn't fit the builder shape
 * (in which case UI falls back to JSON textarea).
 */
export function decompileMatchJson(json: string): RuleCondition[] | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  // Simplest case: literal `true` = empty list (matches anything)
  if (parsed === true) return [];

  const list: unknown[] =
    parsed && typeof parsed === 'object' && parsed !== null && 'and' in parsed
      ? ((parsed as { and: unknown[] }).and ?? [])
      : [parsed];

  const out: RuleCondition[] = [];
  for (const node of list) {
    const c = decompileCondition(node);
    if (!c) return null;
    out.push(c);
  }
  return out;
}

function decompileCondition(node: unknown): RuleCondition | null {
  if (typeof node !== 'object' || node === null) return null;
  const obj = node as Record<string, unknown>;
  const op = Object.keys(obj)[0];
  if (!op) return null;
  const args = obj[op];

  function getField(arg: unknown): string | null {
    if (typeof arg === 'object' && arg !== null && 'var' in arg) {
      const v = (arg as { var: unknown }).var;
      return typeof v === 'string' ? v : null;
    }
    return null;
  }

  if (op === '==' && Array.isArray(args) && args.length === 2) {
    const field = getField(args[0]);
    if (field && typeof args[1] === 'string') {
      return { field, op: 'eq', value: args[1] };
    }
  }
  if (op === '!=' && Array.isArray(args) && args.length === 2) {
    const field = getField(args[0]);
    if (field && typeof args[1] === 'string') {
      return { field, op: 'neq', value: args[1] };
    }
  }
  if (op === 'contains_ci' && Array.isArray(args) && args.length === 2) {
    const field = getField(args[0]);
    if (field && typeof args[1] === 'string') {
      return { field, op: 'contains_ci', value: args[1] };
    }
  }
  if (op === 'regex' && Array.isArray(args) && args.length >= 2) {
    const field = getField(args[0]);
    if (field && typeof args[1] === 'string') {
      const pattern = args[1];
      // Heuristic: anchored regex → starts_with / ends_with
      if (pattern.startsWith('^') && !pattern.endsWith('$')) {
        const unescaped = pattern.slice(1).replace(/\\(.)/g, '$1');
        return { field, op: 'starts_with', value: unescaped };
      }
      if (!pattern.startsWith('^') && pattern.endsWith('$')) {
        const unescaped = pattern.slice(0, -1).replace(/\\(.)/g, '$1');
        return { field, op: 'ends_with', value: unescaped };
      }
      return { field, op: 'regex', value: pattern };
    }
  }
  if (op === '!!' && typeof args === 'object') {
    const field = getField(args);
    if (field) return { field, op: 'exists', value: '' };
  }
  if (op === '!' && typeof args === 'object') {
    const field = getField(args);
    if (field) return { field, op: 'not_exists', value: '' };
  }
  return null;
}
